import { useState, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type {
  AnalyticsMemberRow,
  AnalyticsPeriodNumbers,
  AnalyticsRange,
  AnalyticsSummary,
} from '@shared/analytics';
import { Button } from '@/components/ui/button';
import { isAnalyticsExcluded, setAnalyticsExcluded } from '@/lib/analytics';
import { useAnalyticsData } from './useAnalyticsData';

const RANGES: Array<[AnalyticsRange, string, string]> = [
  ['today', '今天', '昨天'],
  ['7d', '近7天', '前7天'],
  ['30d', '近30天', '前30天'],
];
const panelClass =
  'min-w-0 rounded-xl border border-slate-200 bg-white p-4 sm:p-6';
const number = (value: number) => Number(value || 0).toLocaleString('zh-CN');

function Section({
  title,
  help,
  children,
}: {
  title: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0 space-y-3">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {help && (
          <p className="mt-1 text-xs leading-5 text-slate-500">{help}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function Stat({
  label,
  value,
  previous,
  previousLabel,
  suffix,
}: {
  label: string;
  value: ReactNode;
  previous?: number;
  previousLabel?: string;
  suffix?: ReactNode;
}) {
  return (
    <div className={panelClass}>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-slate-900">
        {value}
        {suffix}
      </p>
      {previous !== undefined && (
        <p className="mt-2 text-xs text-slate-500">
          {previousLabel} {number(previous)}
        </p>
      )}
    </div>
  );
}

function StatRow({
  data,
  previousLabel,
  items,
}: {
  data: AnalyticsSummary;
  previousLabel: string;
  items: Array<[string, keyof AnalyticsPeriodNumbers]>;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {items.map(([label, key]) => (
        <Stat
          key={key}
          label={label}
          value={number(data.current[key])}
          previous={data.previous[key]}
          previousLabel={previousLabel}
        />
      ))}
    </div>
  );
}

function Table({
  head,
  rows,
  empty,
}: {
  head: string[];
  rows: ReactNode[][];
  empty: string;
}) {
  if (!rows.length)
    return (
      <p className={panelClass + ' text-center text-sm text-slate-500'}>
        {empty}
      </p>
    );
  return (
    <div className={panelClass + ' overflow-x-auto p-0 sm:p-0'}>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="bg-slate-50 text-xs text-slate-500">
            {head.map((h, i) => (
              <th
                key={h}
                className={
                  'px-3 py-3 font-medium sm:px-4' + (i ? ' text-right' : '')
                }
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, r) => (
            <tr key={r} className="border-t border-slate-100">
              {cells.map((cell, i) => (
                <td
                  key={i}
                  className={
                    'px-3 py-3 align-middle sm:px-4' +
                    (i ? ' text-right tabular-nums' : ' text-slate-900')
                  }
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function memberName(m: AnalyticsMemberRow) {
  return (
    <span className="block min-w-0">
      <span className="block break-words">{m.remark || '（无备注）'}</span>
      <span className="block text-xs text-slate-500">{m.passcode}</span>
    </span>
  );
}

function Daily({ rows: all }: { rows: AnalyticsSummary['daily'] }) {
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? all : all.slice(0, 7);
  const max = Math.max(1, ...all.map((r) => r.claims));
  return (
    <div className="space-y-2">
      <Table
        head={['日期', '访客', '领取会员', '领取资源']}
        empty="近30天暂无数据"
        rows={rows.map((r) => [
          <span className="tabular-nums">{r.day.slice(5)}</span>,
          number(r.visitors),
          number(r.claimMembers),
          <span
            className="flex items-center justify-end gap-2"
            title={`${r.day} 领取资源 ${r.claims}`}
          >
            <span className="hidden h-2 w-24 justify-end sm:flex">
              <span
                className="h-2 rounded-full bg-orange-500"
                style={{ width: `${(r.claims / max) * 100}%` }}
              />
            </span>
            <span className="w-8">{number(r.claims)}</span>
          </span>,
        ])}
      />
      <Button
        variant="ghost"
        className="min-h-11 w-full text-slate-600"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? '收起，只看近7天' : '展开近30天'}
      </Button>
    </div>
  );
}

function Dashboard({
  data,
  previousLabel,
  onOpenRedemptions,
}: {
  data: AnalyticsSummary;
  previousLabel: string;
  onOpenRedemptions?: () => void;
}) {
  const { members, failures } = data;
  const failed = failures.passcode + failures.quota + failures.other;
  const rate = members.valid
    ? Math.round((members.activeValid / members.valid) * 100)
    : 0;
  const perMember = data.current.claimMembers
    ? (data.current.claims / data.current.claimMembers).toFixed(1)
    : '0';
  return (
    <div className="space-y-8">
      {failed > 0 && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            本期有 {number(failed)} 次领取失败：兑换码无效/冻结/过期{' '}
            {number(failures.passcode)} 次，额度不足 {number(failures.quota)}{' '}
            次，其他 {number(failures.other)} 次。
            {onOpenRedemptions && (
              <button
                type="button"
                onClick={onOpenRedemptions}
                className="ml-2 font-semibold text-orange-800 underline-offset-2 hover:underline"
              >
                去看领取记录 ›
              </button>
            )}
          </p>
        </div>
      )}

      <Section title="流量" help="访客按浏览器计算，同一个人多次打开只算一次。">
        <StatRow
          data={data}
          previousLabel={previousLabel}
          items={[
            ['访客', 'visitors'],
            ['看过主题详情', 'detailVisitors'],
            ['点过客服', 'contactVisitors'],
          ]}
        />
      </Section>

      <Section title="领取" help="同一会员重复领取同一个主题只算一次。">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat
            label="领取的会员"
            value={number(data.current.claimMembers)}
            previous={data.previous.claimMembers}
            previousLabel={previousLabel}
          />
          <Stat
            label="领取的资源"
            value={number(data.current.claims)}
            previous={data.previous.claims}
            previousLabel={previousLabel}
          />
          <Stat
            label="人均领取"
            value={perMember}
            suffix={<span className="ml-1 text-base text-slate-500">个</span>}
          />
        </div>
        <h4 className="pt-2 text-sm font-medium text-slate-700">每日情况</h4>
        <Daily rows={data.daily} />
      </Section>

      <Section
        title="会员活跃"
        help="有效会员 = 未冻结且未过期。沉睡 = 有效但近30天一次都没领取，适合客服主动提醒。"
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="有效会员" value={number(members.valid)} />
          <Stat
            label="本期活跃"
            value={number(members.activeValid)}
            suffix={
              <span className="ml-2 text-base text-slate-500">{rate}%</span>
            }
          />
          <Stat label="沉睡会员" value={number(members.dormant)} />
          <Stat
            label="新开会员"
            value={number(data.current.newMembers)}
            previous={data.previous.newMembers}
            previousLabel={previousLabel}
          />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="min-w-0 space-y-2">
            <h4 className="text-sm font-medium text-slate-700">
              本期领取最多的会员
            </h4>
            <Table
              head={['会员', '领取', '最近领取']}
              empty="本期还没有会员领取"
              rows={data.topMembers.map((m) => [
                memberName(m),
                number(m.claims),
                m.lastClaimDay || '-',
              ])}
            />
          </div>
          <div className="min-w-0 space-y-2">
            <h4 className="text-sm font-medium text-slate-700">
              沉睡会员（快到期的在前）
            </h4>
            <Table
              head={['会员', '到期日', '最近领取']}
              empty="没有沉睡会员"
              rows={data.dormantMembers.map((m) => [
                memberName(m),
                m.expiryDate,
                m.lastClaimDay || '从未领取',
              ])}
            />
          </div>
        </div>
      </Section>

      <Section title="热门主题" help="按本期看过详情的人数排序。">
        <Table
          head={['主题', '看过的人', '领取的会员']}
          empty="本期暂无主题访问"
          rows={data.topTopics.map((t, i) => [
            <span className="break-words">
              {i + 1}. {t.name}
            </span>,
            number(t.viewers),
            number(t.claims),
          ])}
        />
      </Section>
    </div>
  );
}

export function AnalyticsPanel({ onOpenRedemptions }: { onOpenRedemptions?: () => void } = {}) {
  const { range, data, error, busy, setRange, refresh } = useAnalyticsData();
  const [excluded, setExcluded] = useState(isAnalyticsExcluded);
  const previousLabel = RANGES.find(([r]) => r === range)![2];
  return (
    <section aria-label="运营数据" className="min-w-0 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground">运营数据</h2>
          <p className="mt-1 text-sm text-muted-foreground">每个数字都和上一个同样长度的时段对比</p>
        </div>
        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label="时间范围"
            className="flex rounded-lg bg-slate-100 p-1"
          >
            {RANGES.map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={range === value}
                onClick={() => setRange(value)}
                className={
                  'min-h-9 rounded-md px-3 text-sm transition-colors ' +
                  (range === value
                    ? 'bg-white font-medium text-orange-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900')
                }
              >
                {label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-11 w-11"
            aria-label="刷新"
            onClick={refresh}
            disabled={busy}
          >
            <RefreshCw aria-hidden className={busy ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {error ? (
        <div role="alert" className={panelClass + ' space-y-3'}>
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="outline" className="min-h-11" onClick={refresh}>
            重试
          </Button>
        </div>
      ) : data ? (
        <Dashboard data={data} previousLabel={previousLabel} onOpenRedemptions={onOpenRedemptions} />
      ) : (
        <p
          role="status"
          className={panelClass + ' py-12 text-center text-sm text-slate-500'}
        >
          正在读取数据…
        </p>
      )}

      <label className="flex min-h-11 items-center gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          className="h-4 w-4 accent-orange-600"
          checked={excluded}
          onChange={(e) => {
            setAnalyticsExcluded(e.target.checked);
            setExcluded(e.target.checked);
          }}
        />
        本浏览器的访问不计入统计（管理员登录后会自动勾选）
      </label>
    </section>
  );
}
